import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { DEFAULT_CONTENT, deepMerge } from "./defaultContent";
import { cache } from "./cache";

const ContentCtx = createContext(null);

const CACHE_KEY = "content";

// Retry with brief backoff so flaky mobile networks don't leave the page
// showing only the bundled defaults.
async function fetchWithRetry(path, attempts = 3) {
  let lastErr;
  for (let i = 0; i < attempts; i++) {
    try {
      const r = await api.get(path);
      return r.data;
    } catch (e) {
      lastErr = e;
      await new Promise((res) => setTimeout(res, 250 * (i + 1) * (i + 1)));
    }
  }
  throw lastErr;
}

function buildInitial() {
  // Highest priority: inline bootstrap data injected via /api/bootstrap.js.
  // This is the most reliable path (loads as a static-cached .js asset
  // before React mounts), so the public site renders the owner's real
  // CMS edits with zero API dependency.
  if (typeof window !== "undefined" && window.__ATC_BOOTSTRAP__?.content) {
    return deepMerge(DEFAULT_CONTENT, window.__ATC_BOOTSTRAP__.content);
  }
  // Next: previously-cached API response from localStorage.
  const cached = cache.get(CACHE_KEY);
  if (cached) return deepMerge(DEFAULT_CONTENT, cached);
  // Last resort: bundled defaults.
  return DEFAULT_CONTENT;
}

export function ContentProvider({ children }) {
  const [content, setContent] = useState(buildInitial);

  const refresh = useCallback(async () => {
    try {
      const data = await fetchWithRetry("/content");
      if (data && typeof data === "object") {
        cache.set(CACHE_KEY, data);
        setContent(deepMerge(DEFAULT_CONTENT, data));
      }
    } catch {
      // Keep whatever we already have (cache or bundled default)
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return <ContentCtx.Provider value={{ content, refresh }}>{children}</ContentCtx.Provider>;
}

export function useContent() {
  const ctx = useContext(ContentCtx);
  return ctx?.content || DEFAULT_CONTENT;
}

/** Get a nested value with a fallback default. Path = "home.hero_title_line_1" */
export function pick(obj, path, fallback = "") {
  if (!obj) return fallback;
  const parts = path.split(".");
  let cur = obj;
  for (const p of parts) {
    if (cur == null) return fallback;
    cur = cur[p];
  }
  if (cur === undefined || cur === null) return fallback;
  if (typeof cur === "string" && cur.trim() === "") return fallback;
  if (Array.isArray(cur) && cur.length === 0) return fallback;
  return cur;
}
